// src/components/ReferralWithdrawalCard.jsx
import React, { useEffect, useState } from "react";
import API from "../utils/api";

const REFERRAL_THRESHOLD = 10;
const AMOUNT_PER_REFERRAL = 5000;

export default function ReferralWithdrawalCard({ currencySymbol, theme }) {
  const [eligibleCount, setEligibleCount] = useState(0);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const fetchStatus = async () => {
    try {
      const res = await API.get("/referral-withdrawal/my-status");
      // ✅ FIXED: /my-status now returns { eligibleCount, withdrawals }
      // instead of a bare array — this was the source of the crash,
      // since the old code called array methods directly on res.data.
      setEligibleCount(res.data?.eligibleCount || 0);
      setWithdrawals(Array.isArray(res.data?.withdrawals) ? res.data.withdrawals : []);
    } catch (err) {
      console.error("Fetch referral withdrawals error:", err.message);
      // ✅ NEW: fail safe rather than crash — keep prior state, don't
      // let a network hiccup take down the whole dashboard.
      setEligibleCount(0);
      setWithdrawals([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const hasPending = withdrawals.some((w) => w.status === "pending");
  const isEligible = eligibleCount >= REFERRAL_THRESHOLD;
  const withdrawableAmount = eligibleCount * AMOUNT_PER_REFERRAL;

  const requestWithdrawal = async () => {
    setRequesting(true);
    setStatusMsg("");
    try {
      const res = await API.post("/referral-withdrawal/request", {});
      setStatusMsg(res.data.message);
      fetchStatus();
    } catch (err) {
      setStatusMsg(err.response?.data?.message || "Failed to submit request");
    } finally {
      setRequesting(false);
      setTimeout(() => setStatusMsg(""), 5000);
    }
  };

  const cardClass = theme?.card || "bg-white/[0.04] border border-white/10";
  const accentClass = theme?.accent || "bg-emerald-500 hover:bg-emerald-400 text-black";

  if (loading) {
    return (
      <div className={`p-5 rounded-2xl backdrop-blur-md shadow-xl ${cardClass}`}>
        <h3 className="text-sm font-semibold mb-3">Referral Withdrawal</h3>
        <p className="text-xs opacity-50">Loading...</p>
      </div>
    );
  }

  return (
    <div className={`p-5 rounded-2xl backdrop-blur-md shadow-xl ${cardClass}`}>
      <h3 className="text-sm font-semibold mb-3">Referral Withdrawal</h3>

      <div className="flex items-center justify-between mb-2 text-sm">
        <span className="opacity-60">Approved referrals</span>
        <span className="font-bold">
          {isEligible ? `${eligibleCount} referrals ✓` : `${eligibleCount} / ${REFERRAL_THRESHOLD}`}
        </span>
      </div>

      <div className="w-full h-1.5 rounded-full bg-white/10 mb-4 overflow-hidden">
        <div
          className="h-full bg-emerald-400 transition-all"
          style={{ width: `${Math.min(100, (eligibleCount / REFERRAL_THRESHOLD) * 100)}%` }}
        />
      </div>

      {!isEligible && (
        <p className="text-xs opacity-50 mb-3">
          {REFERRAL_THRESHOLD - eligibleCount} more approved {REFERRAL_THRESHOLD - eligibleCount === 1 ? "referral" : "referrals"} needed to unlock withdrawal.
          Only referred users who have been approved (paid and verified) count.
        </p>
      )}

      {isEligible && !hasPending && (
        <>
          <p className="text-xs opacity-60 mb-3">
            You're eligible to withdraw {currencySymbol}{withdrawableAmount.toLocaleString()} ({eligibleCount} × {currencySymbol}{AMOUNT_PER_REFERRAL.toLocaleString()}).
          </p>
          <button
            onClick={requestWithdrawal}
            disabled={requesting}
            className={`w-full py-2.5 rounded-xl font-semibold text-sm transition disabled:opacity-50 ${accentClass}`}
          >
            {requesting ? "Submitting..." : "Withdraw Referral Bonus"}
          </button>
        </>
      )}

      {isEligible && hasPending && (
        <p className="text-xs opacity-60 italic">
          You have a pending withdrawal request awaiting admin processing.
        </p>
      )}

      {!isEligible && (
        <p className="text-xs opacity-40 italic mt-1">
          Share your referral link above to start earning.
        </p>
      )}

      {statusMsg && <p className="text-xs mt-2 opacity-70">{statusMsg}</p>}

      {withdrawals.length > 0 && (
        <div className="mt-4 pt-4 border-t border-white/10 space-y-2">
          <p className="text-xs font-semibold opacity-50 mb-1">History</p>
          {withdrawals.map((w) => (
            <div key={w._id} className="flex items-center justify-between text-xs">
              <span className="opacity-60">
                {currencySymbol}{w.amount?.toLocaleString()} · {new Date(w.createdAt).toLocaleDateString()}
              </span>
              <span
                className={`font-bold uppercase px-2 py-0.5 rounded-full text-[10px] ${
                  w.status === "paid" ? "bg-emerald-600 text-white" : "bg-yellow-600 text-black"
                }`}
              >
                {w.status === "paid" ? "Sent" : "Pending"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
